// src/lib/hooks/usePauseMediaOnHidden.ts
'use client'

import { useRef, type RefObject } from 'react'
import { useIsomorphicLayoutEffect } from '~/lib/hooks/useIsomorphicLayoutEffect'

interface PauseMediaOptions {
  /**
   * Resume playback when the route becomes visible again IF the media was
   * playing when it was hidden. Use for decorative autoplay loops (e.g. demo
   * tiles) that should keep looping on return. Leave `false` (default) for
   * user-controlled players (controls / click-to-play) so the user decides when
   * to resume — the playback position is preserved either way.
   */
  resumeOnShow?: boolean
}

/**
 * Pause a native media element when its route is hidden by Cache Components'
 * `<Activity>` (and on unmount), since `display: none` alone never stops
 * playback — audio keeps playing on a hidden route. The DOM node is preserved,
 * so the current time is retained for resume.
 *
 * `<Activity>` runs effect cleanups on hide and re-runs effects on show, so this
 * works even when the element mounts AFTER first render (e.g. an
 * IntersectionObserver-gated tile): the cleanup/effect body read `ref.current`
 * FRESH at hide/show time rather than capturing it (which is why the
 * exhaustive-deps "copy ref.current to a variable" suggestion is intentionally
 * not followed here — capturing would pin a stale/null element).
 */
export function usePauseMediaOnHidden(
  ref: RefObject<HTMLMediaElement | null>,
  { resumeOnShow = false }: PauseMediaOptions = {},
): void {
  const wasPlayingRef = useRef(false)

  useIsomorphicLayoutEffect(() => {
    // Effect body runs on first mount AND on every hide→visible transition.
    // Resume only when returning from a hide during which it was playing, and
    // never auto-restart a finished video.
    const el = ref.current
    if (resumeOnShow && el && wasPlayingRef.current && el.paused && !el.ended) {
      void el.play().catch(() => {
        /* autoplay can be refused; ignore */
      })
    }
    wasPlayingRef.current = false

    // Cleanup runs synchronously when the route is hidden (and on unmount).
    return () => {
      // Read the CURRENT element at hide time (not a captured copy): it may have
      // mounted after this effect ran, e.g. IntersectionObserver-gated tiles.
      const node = ref.current
      if (!node) return
      wasPlayingRef.current = !node.paused
      node.pause()
    }
  }, [ref, resumeOnShow])
}
